import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useI18n } from '../hooks/useI18n';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
  Box,
  Chip,
  Button,
  Tooltip,
} from '@mui/material';

import NotificationsIcon from '@mui/icons-material/Notifications';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import api from '../api';


const NOTIFICATIONS_TYPES = {
  'system': { icon: <PrivacyTipOutlinedIcon />, color: 'primary', label: 'Sistema' },
  'user': { icon: <AccountCircleOutlinedIcon />, color: 'secondary', label: 'Utente' },
  'alert': { icon: <AnnouncementOutlinedIcon />, color: 'error', label: 'Allerta' },
  'report': { icon: <SummarizeOutlinedIcon />, color: 'info', label: 'Report' }
};


const NotificationItem = ({ notification, onNotificationClick }) => {
  const notificationType = NOTIFICATIONS_TYPES[notification.notification_type] || NOTIFICATIONS_TYPES['system'];
  const isExternal = notification.url?.startsWith('http');

  return (
    <MenuItem
      onClick={() => onNotificationClick(notification)}
      sx={{
        whiteSpace: 'normal',
        py: 1.5,
        px: 2,
        alignItems: 'flex-start',
        gap: 2,
        '&:hover': {
          backgroundColor: 'action.hover',
        }
      }}
    >
      <Box sx={{ mt: 0.5, color: `${notificationType.color}.main` }}>
        {notificationType.icon}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.5 }}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          {notification.verb}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip 
              label={notification.actor.username} 
              variant="outlined" 
              size="small" 
              sx={{ fontSize: '0.7rem' }}
            />
            <Chip 
              label={notificationType.label} 
              color={notificationType.color}
              size="small" 
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {new Date(notification.created_at).toLocaleString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
            {isExternal && <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
          </Box>
        </Box>
      </Box>
    </MenuItem>
  );
};


export default function NotificationMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n('features');
  const isAuthenticated = useSelector((state) => state.user.isAuth);
  const navigate = useNavigate();
  
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchNotifications = useCallback(() => {
    if (!isAuthenticated) return;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoading(true);
    
    api.get('/notifications/unread/', {
      signal: abortControllerRef.current.signal
    })
      .then(res => {
        if (isMountedRef.current) {
          setNotifications(res.data.notifications || []);
        }
      })
      .catch(err => {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          return;
        }
        
        if (isMountedRef.current) {
          console.error('Errore caricamento notifiche:', err);
          enqueueSnackbar(
            t('notifications.errorLoading', { defaultValue: 'Errore nel caricamento delle notifiche' }), 
            { variant: 'error' }
          );
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setLoading(false);
        }
      });
  }, [isAuthenticated, enqueueSnackbar, t]);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (isAuthenticated) {
      fetchNotifications();
      
      const interval = setInterval(fetchNotifications, 60000);
      
      return () => {
        isMountedRef.current = false;
        clearInterval(interval);
        
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [isAuthenticated, fetchNotifications]);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    api.post(`/notifications/${notification.id}/mark_read/`)
      .then(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        handleCloseMenu();

        const url = notification.url;

        if (url?.startsWith('http')) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } 
        else if (url && url !== '/') {
          navigate(url);
        }
      })
      .catch(err => {
        console.error('Errore segna come letta:', err);
        enqueueSnackbar(
          t('notifications.errorMarkRead', { defaultValue: 'Errore nella marcatura come letta' }), 
          { variant: 'error' }
        );
      });
  };

  const handleMarkAllAsRead = () => {
    api.post('/notifications/mark_all_read/')
      .then((res) => {
        setNotifications([]);
        enqueueSnackbar(res.data.message, { variant: 'success' });
        setTimeout(fetchNotifications, 500);
      })
      .catch(err => {
        console.error('Errore segna tutte:', err);
        enqueueSnackbar(
          t('notifications.errorMarkAll', { defaultValue: 'Errore nella marcatura' }), 
          { variant: 'error' }
        );
      });
    handleCloseMenu();
  };

  const handleViewAll = () => {
    handleCloseMenu();
    navigate('/notifications');
  };

  const unreadCount = notifications.length;

  if (!isAuthenticated) return null;

  return (
    <>
      <Tooltip title={t('notifications.title', { defaultValue: 'Notifiche' })}>
        <IconButton color="inherit" onClick={handleOpenMenu}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{ 
          style: { 
            maxHeight: 500, 
            width: '400px',
            maxWidth: '95vw'
          } 
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          px: 2, 
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('notifications.title', { defaultValue: 'Notifiche' })}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              {t('notifications.markAllRead', { defaultValue: 'Segna tutte' })}
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.noNotifications', { defaultValue: 'Nessuna notifica non letta' })}
            </Typography>
          </MenuItem>
        ) : (
          notifications.map(notification => (
            <NotificationItem 
              key={notification.id} 
              notification={notification} 
              onNotificationClick={handleNotificationClick} 
            />
          ))
        )}

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button 
            fullWidth 
            variant="text" 
            onClick={handleViewAll}
            sx={{ textTransform: 'none' }}
          >
            {t('notifications.viewAll', { defaultValue: 'Visualizza tutte le notifiche' })}
          </Button>
        </Box>
      </Menu>
    </>
  );
}